import React, { useState, useEffect } from 'react';
import { getCoinImageUrl } from '../lib/imageUrl';
import './ImageBox.css';

interface ImageBoxProps {
  imagePath?: string | null;
  denomination?: string;
  year?: number;
  coinId?: string;
  isAnimating?: boolean;
  isExiting?: boolean;
  shouldFlip?: boolean;
  enterDurationMs?: number;
  exitDurationMs?: number;
  auraGradient?: string;
  onFlipComplete?: () => void;
  onExitComplete?: () => void;
}

export function getCommonReverseImagePath(denomination: string = '2euro', year?: number): string {
  const denom = denomination || '2euro';

  // 1c, 2c, 5c (Globe design) did not change in 2007
  if (['1cent', '2cent', '5cent'].includes(denom)) {
    return getCoinImageUrl(`common/common_${denom}.webp`, 'full') || '';
  }

  // 10c, 20c, 50c, 1€, 2€ (EU map design) changed in 2007 to include the full map of Europe
  const prefix = (!year || year < 2007) ? 'common' : 'newcommon';
  return getCoinImageUrl(`common/${prefix}_${denom}.webp`, 'full') || '';
}

export const ImageBox: React.FC<ImageBoxProps> = ({
  imagePath,
  denomination = '2euro',
  year,
  coinId,
  isAnimating = false,
  isExiting = false,
  shouldFlip = true,
  enterDurationMs,
  exitDurationMs,
  auraGradient,
  onFlipComplete,
  onExitComplete
}) => {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [imagePath, coinId]);

  const obverseSrc = getCoinImageUrl(imagePath, 'full') || '';
  const showObverseImage = Boolean(obverseSrc) && !imgError;
  const reverseSrc = getCommonReverseImagePath(denomination, year);

  let flipperClass = 'revealed';
  if (isExiting) {
    flipperClass = 'exiting';
  } else if (isAnimating) {
    flipperClass = shouldFlip ? 'animating' : 'animating-slide';
  }

  const flipperStyle: React.CSSProperties = {
    ...(enterDurationMs ? { ['--coin-flip-duration' as any]: `${enterDurationMs}ms` } : {}),
    ...(exitDurationMs ? { ['--coin-exit-duration' as any]: `${exitDurationMs}ms` } : {})
  };

  return (
    <div className="image-box">
      <div className="coin-stage">
        <div
          key={coinId || imagePath || 'initial'}
          className={`coin-flipper ${flipperClass}`}
          style={flipperStyle}
          onAnimationEnd={(e) => {
            if (e.animationName === 'coinExitRight') {
              if (onExitComplete) {
                onExitComplete();
              }
            } else if (isAnimating && onFlipComplete) {
              onFlipComplete();
            }
          }}
        >
          {/* Reverse Face (European map with 2 Euro denomination) */}
          <div className="coin-face coin-face-reverse">
            <img
              src={reverseSrc}
              alt="2 Euro Reverse"
              className="coin-img"
            />
          </div>

          {/* Obverse Face (National or Commemorative design) */}
          <div className="coin-face coin-face-obverse">
            {auraGradient && auraGradient !== 'none' && (
              <div className="coin-aura" style={{ background: auraGradient }} />
            )}
            {showObverseImage ? (
              <img
                id="coinImg"
                className="coin-img"
                src={obverseSrc}
                alt="Coin obverse"
                onError={() => setImgError(true)}
              />
            ) : (
              <span id="imgPlaceholder" className="img-placeholder">
                image
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
